import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button, Grid, Modal, SvgIcon, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import toast from 'react-hot-toast';
import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import loader from 'src/components/LoaderImg/loader.gif';
import { goalBankTreatment } from 'src/config';
import { paths } from 'src/paths';
import {
  checkIsReformAction,
  getTargetSignatureAccessAction,
  pullUuidAction,
  resetForLoaderDocumentation,
  resetUuidAction,
} from 'src/redux/actions/documentationAction';
import { checkSupervisorPermission } from 'src/utils/checkSignatureAccess';
import isEvalFormType from 'src/utils/isEvalFormType';
import DocumentTemplateUnavailable from './document-template-unavailable';
import SucessModal from '../sucess-modal';
import CopyGoalWidget from './copy-goal-widget';
// import CopyTextBank from '../copy-text-bank';
// import { DrawerMain } from '../copy-text-bank/drawer-configuration';
// import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useCurrentUser from 'src/hooks/use-current-user';
import useFeatureToggle from 'src/hooks/use-feature-toggle';
import ReForm from '../reform';
import AddendumNote from '../AddendumNote';
import { getDSNNoteSummary, resetDsnSummary } from 'src/redux/actions/therapist-dashboard-action';
import moment from 'moment';
import DSNSummaryModal from '../dailySessionNotes/DSN summary/DSNSummaryModal';
import { getOldestRequiredDocumentDate } from 'src/utils/getOldestRequiredDocumentUuid';
import { humanizeFormType } from 'src/utils/humanizeFormType';
import WarningModal from '../therapyPlanning_2.0/commonComponent/warning-modal';

const loaderCSS: CSSProperties = {
  position: 'fixed',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  WebkitUserSelect: 'none',
  KhtmlUserSelect: 'none',
  MozUserSelect: 'none',
  userSelect: 'none',
  zIndex: 9999,
};

function CreateDocumentPrview() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { patients, patientDetails, episodeOfCare, details } = paths;

  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  // const { episodeOfCareId, document, patientData, activeTab, requiredDocument } = location?.state;
  const { episodeOfCareId, document, patientData, activeTab, requiredDocument } = useSelector(
    (state: any) => state.eocCreateDocumentDataReducer
  );
  const getSignatureAccess = useSelector((state: any) => state.getSignatureAccessReducer);
  //  BRK-2074: an on-behalf create (the persisted hand-off document carries onBehalfReason +
  //  onBehalfOfProviderId) mints TARGET-keyed and reads the TARGET's t1 map for isForSupervisor.
  const targetSignatureAccess = useSelector((state: any) => state.getTargetSignatureAccessReducer);
  const isOnBehalfCreate = Boolean(document?.onBehalfReason && document?.onBehalfOfProviderId);
  const onBehalfMapsReady = Boolean(targetSignatureAccess?.response?.data);
  const pullUuidResponse = useSelector((state: any) => state.pullUuidRducder);
  const isDocLoader = useSelector((state: any) => state.loaderDocumenattionReducer);
  const [checkGoalBankToggle] = useFeatureToggle(goalBankTreatment, 'FACILITY');

  const [initialized, setInitialized] = useState(false);

  // Reform-based addendum notes are tagged by the backend with the ADDENDUM_NOTE_REFORM
  // formType. Legacy ADDENDUM_NOTE was a Jotform form and can no longer be created.
  const useReformAddendumNote = document?.formType === 'ADDENDUM_NOTE_REFORM';

  useEffect(() => {
    if (!document) return;
    dispatch(checkIsReformAction(document?.formType?.includes('REFORM')));
  }, [document]);

  useEffect(() => {
    if (!initialized) {
      dispatch(resetUuidAction());
      setInitialized(true);
    }
  }, [initialized, dispatch]);

  const [providerId, discipline] = useCurrentUser(['providerId', 'discipline']);

  const backToEOCDetails = () => {
    dispatch(resetUuidAction());
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
      state: {
        episodeOfCareId: episodeOfCareId,
        getPatientId,
      },
    });
  };

  const [openErrorModal, setOpenErrorModal] = useState(false);
  const closeErrorModal = () => {
    setOpenErrorModal(false);
    dispatch(resetForLoaderDocumentation());
  };

  useEffect(() => {
    if (!isDocLoader.loader && isDocLoader.response === null) return;
    if (isDocLoader.error) {
      setOpenErrorModal(true);
      return;
    }
    if (!isDocLoader.loader) {
      dispatch(resetForLoaderDocumentation());
      backToEOCDetails();
    }
  }, [isDocLoader]);

  //  BRK-2074: a reload straight into the preview loses the (unpersisted) target maps the
  //  dialog fetched — refetch them for the hand-off's target so the mint below can proceed.
  useEffect(() => {
    if (isOnBehalfCreate && !onBehalfMapsReady) {
      dispatch(
        getTargetSignatureAccessAction([], {
          discipline: activeTab,
          providerId: document.onBehalfOfProviderId,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document]);

  //  The mint below WAITS on those maps — a failed fetch must not strand the page on an empty
  //  preview forever. Bail back to the EoC page; re-entering the flow re-collects everything.
  useEffect(() => {
    if (isOnBehalfCreate && targetSignatureAccess?.isError) {
      toast.error(
        "Could not load the selected therapist's document settings. Please start the document again."
      );
      backToEOCDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetSignatureAccess?.isError]);

  useEffect(() => {
    if (!document) return;
    //  BRK-2074: the on-behalf mint must not fire with the CALLER's t1 map — wait for the
    //  target's (the boolean flips exactly once, so this cannot double-mint).
    if (isOnBehalfCreate && !onBehalfMapsReady) return;
    let fetchMostRecent = false;

    if (document.reformId) {
      if (
        document.formType === 'EVALUATION_REFORM_PT' ||
        document.formType === 'EVALUATION_REFORM_OT' ||
        document.formType === 'EVALUATION_REFORM_SLP' ||
        document.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_PT' ||
        document.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_OT' ||
        document.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_SLP' ||
        document.formType === 'RECERTIFICATION_REFORM_OT' ||
        document.formType === 'RECERTIFICATION_REFORM_PT' ||
        document.formType === 'RECERTIFICATION_REFORM_SLP' ||
        document.formType === 'DISCHARGE_REFORM_OT' ||
        document.formType === 'DISCHARGE_REFORM_PT' ||
        document.formType === 'DISCHARGE_REFORM_SLP'
      ) {
        fetchMostRecent = true;
      }
    }

    dispatch(
      pullUuidAction(
        {
          uuid: null,
          //  BRK-2074: a CREATE webhook is TARGET-keyed — prefill, supervision, and authorship
          //  all derive from the webhook's provider server-side (unlike edit, K5, which stays
          //  actor-keyed). onBehalfReason is the BRK-2072 discriminator: the backend verifies
          //  the COMPLIANCE_DOCUMENT_ON_BEHALF grant and stamps the reason on the webhook row.
          providerId: isOnBehalfCreate ? document.onBehalfOfProviderId : providerId,
          formId: document?.formId,
          episodeOfCareId,
          type: activeTab,
          isForSupervisor: checkSupervisorPermission(
            (isOnBehalfCreate ? targetSignatureAccess : getSignatureAccess).response?.data?.t1,
            document?.formType
          ),
          entityType: 'EOC',
          documentType: document?.formType,
          fromRequired: requiredDocument ? true : false,
          ...(isOnBehalfCreate ? { onBehalfReason: document.onBehalfReason } : {}),
        },
        {
          fetchMostRecent,
        }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, onBehalfMapsReady]);

  const getCleanUuid = () => {
    return pullUuidResponse?.response?.trim() || '';
  };

  const [openTextBank, setOpenTextBank] = React.useState(false);
  const [showPayerWarningModal, setShowPayerWarningModal] = useState(false);
  const [payerErrorEpisodeOfCareId, setPayerErrorEpisodeOfCareId] = useState<string | undefined>(
    undefined
  );

  const handleTextBankOpen = () => {
    setOpenTextBank(true);
  };

  const handleTextBankClose = () => {
    setOpenTextBank(false);
  };

  const handlePayerError = (error: {
    error?: string;
    errorCode?: string;
    errorData?: any;
    episodeOfCareId?: string;
  }) => {
    if (error.errorCode === 'NO_PAYER_ON_CERTIFICATION_START_DATE') {
      setPayerErrorEpisodeOfCareId(error.episodeOfCareId);
      setShowPayerWarningModal(true);
    }
  };

  const handleAssignPayer = () => {
    setShowPayerWarningModal(false);
    navigate(`${patients}/${patientDetails}${paths.payers}`, {
      state: {
        patientId: getPatientId,
      },
    });
  };

  const handleCancelPayerModal = () => {
    setShowPayerWarningModal(false);
    // If episodeOfCareId exists, redirect back to episode of care
    // Otherwise, redirect to therapist dashboard
    if (payerErrorEpisodeOfCareId) {
      backToEOCDetails();
    } else {
      navigate(paths.dashboard);
    }
  };

  const isReformsEnabled = () => {
    if (!pullUuidResponse?.response || !document?.reformId) {
      return false;
    }

    // Transitional evaluations render natively only in their reform flavour; the legacy
    // (Jotform) TRANSITIONAL_EVALUATION_* types can't be opened.
    if (document?.formType?.includes('TRANSITIONAL_EVALUATION')) {
      return document?.formType?.includes('_REFORM_');
    }

    // For all other document types
    if (
      document?.formType === 'EVALUATION_REFORM_PT' ||
      document?.formType === 'EVALUATION_REFORM_OT' ||
      document?.formType === 'EVALUATION_REFORM_SLP' ||
      document?.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_PT' ||
      document?.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_OT' ||
      document?.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_SLP' ||
      document?.formType === 'RECERTIFICATION_REFORM_OT' ||
      document?.formType === 'RECERTIFICATION_REFORM_PT' ||
      document?.formType === 'RECERTIFICATION_REFORM_SLP' ||
      document?.formType === 'DISCHARGE_REFORM_OT' ||
      document?.formType === 'DISCHARGE_REFORM_PT' ||
      document?.formType === 'DISCHARGE_REFORM_SLP'
    ) {
      return true;
    }

    return false;
  };
  const dsnSummary = useSelector((state: any) => state.getDsnSummaryReducer);
  const [openSummary, setOpenSummary] = useState(false);

  const episodeOfCareRequiredDocumentData = useSelector(
    (state: any) => state.getEpisodeOfCareRequiredDocumentReducer
  );

  const oldestUuid = useMemo(() => {
    return episodeOfCareRequiredDocumentData?.response?.content
      ? getOldestRequiredDocumentDate([...episodeOfCareRequiredDocumentData.response.content])
      : undefined;
  }, [episodeOfCareRequiredDocumentData?.response?.content]);

  const handleOpenDsnSummary = () => {
    dispatch(resetDsnSummary());
    dispatch(
      getDSNNoteSummary({
        patientId: getPatientId,
        type: 'progress-note',
        params: {
          discipline: activeTab,
          localDate: oldestUuid ? oldestUuid : moment(new Date()).format('YYYY-MM-DD'),
        },
      })
    );
    setOpenSummary(true);
  };

  return (
    <>
      {isDocLoader.loader && (
        <Grid sx={loaderCSS}>
          <img
            src={loader}
            alt="loader.."
          />
        </Grid>
      )}

      <Grid
        container
        justifyContent={'space-between'}
        sx={{ width: '100%' }}
      >
        <Stack
          gap="1.5rem"
          sx={{ padding: '0 4rem', width: '100%' }}
        >
          <Button
            sx={{
              justifyContent: 'flex-start',
              color: '#111927',
              width: 'max-content',
              padding: '0px',
            }}
            onClick={backToEOCDetails}
            startIcon={
              <SvgIcon>
                <ArrowBackIcon sx={{ color: '#6C737F' }} />
              </SvgIcon>
            }
          >
            <Typography variant="subtitle1">
              Back to Episode Of Care Details -
              {` ${patientData?.lastName ?? ''} ${patientData?.firstName ?? ''}`}
            </Typography>
          </Button>
          <Stack
            flexDirection="row"
            gap="1rem"
            alignItems="center"
            justifyContent={'space-between'}
            sx={{ width: '100%' }}
          >
            <Typography variant="h5">
              Create Document - {humanizeFormType(document?.formType)}
            </Typography>
            {document?.formType?.includes('WEEKLY_PROGRESS_NOTE') && (
              <Button
                color="primary"
                size="small"
                onClick={() => handleOpenDsnSummary()}
                variant="outlined"
              >
                DSN Summary
              </Button>
            )}
          </Stack>
        </Stack>
        {checkGoalBankToggle && <CopyGoalWidget />}
      </Grid>
      {/* <Box sx={{ display: 'flex', height: '100%' }}>
      <CssBaseline />
        // <DrawerMain open={openTextBank}> */}
      <Box
        sx={{ height: '100%' }}
        p={3}
      >
        {isReformsEnabled() ? (
          <Box>
            {initialized && (
              <ReForm
                uuid={getCleanUuid()}
                formId={document?.reformId}
                onSubmitError={handlePayerError}
              />
            )}
          </Box>
        ) : useReformAddendumNote ? (
          <AddendumNote
            formId={document?.reformId}
            webhookUuid={getCleanUuid()}
            patientData={patientData}
          />
        ) : isEvalFormType(document?.formType) && pullUuidResponse?.response ? (
          // BRK-1921: evaluations are reform-only. Without a reformId this used to render an
          // empty iframe (blank pane), hiding a template misconfiguration.
          // BRK-2211: gated on the minted uuid — isReformsEnabled() is false until it arrives, so
          // an ungated branch flashed this dead end before every evaluation's form loaded.
          <DocumentTemplateUnavailable
            onBack={backToEOCDetails}
            backLabel="Back to Episode of Care"
          />
        ) : pullUuidResponse?.response ? (
          // Jotform is gone: any document type that can't render natively (a legacy Jotform
          // type, or a Reform type missing its reformId) gets an explicit dead end instead of
          // an iframe. Gated on the minted uuid so it doesn't flash while that is loading.
          <DocumentTemplateUnavailable
            onBack={backToEOCDetails}
            backLabel="Back to Episode of Care"
          />
        ) : null}
      </Box>
      {/* </DrawerMain>
        <CopyTextBank
          open={openTextBank}
          handleDrawerClose={handleTextBankClose}
          source='under_eoc'
        /> */}
      {/* </Box> */}

      <Modal open={openErrorModal}>
        <SucessModal
          close={closeErrorModal}
          isSucess={false}
          title="Create a Document"
          message={isDocLoader?.response}
        />
      </Modal>
      <DSNSummaryModal
        open={openSummary}
        title={`DSN Summary | ${discipline}`}
        onClose={() => setOpenSummary(false)}
        data={dsnSummary?.response}
      />
      <WarningModal
        open={showPayerWarningModal}
        onClose={handleCancelPayerModal}
        title="Payer Required"
        message="A certification must be associated with an active payer. Please assign a payer for the selected date before continuing."
        buttonText="Assign Payer"
        cancelBtn="Cancel"
        handleSaveBtn={handleAssignPayer}
      />
    </>
  );
}

export default CreateDocumentPrview;
